import {
  IInjectable,
  Ng1StateDeclaration,
  Ng1ViewDeclaration,
  StateParams,
  StateRegistry,
  StateService,
} from "@uirouter/angularjs";
import { IServiceProvider } from "angular";
import {
  vsName as serverUtilityName,
  ServerUtility,
} from "../../records/server/serverUtility";
import {
  GridConfiguration,
  GridConfigurationFactory,
  vsComponentName as RecordGridViewComponentName,
} from "../../records/views/recordGridView";
import { vsComponentName as machineDetailContractComponentName } from "../views/infoNew";
import {
  FormViewController,
  FormViewService,
  vsServiceName as FormViewServiceName,
  getResolveObjectFromGridConfiguration,
} from "../../records/forms/formView";
import { Record } from "../../records/server/crud";
import {
  ColumnDefinitionService,
  vsName as ColumnDefinitionServiceName,
} from "../../records/grids/columns/columnDefinitionService";
export const vsServiceName = "contractService";
export const vsProviderName = `${vsServiceName}Provider`;

const baseState = "main.contract.type";

const createState = `${baseState}.create`;
const gridState = `${baseState}.grid`;

const infoState = `${baseState}.info`;
const editState = `${infoState}.edit`;
const infoRedirectState = `${infoState}.redirect`;

export class ContractServiceProvider implements IServiceProvider {
  static $inject = [];
  constructor() {}

  private readonly _contractTypes: IContractType[] = [];
  private readonly _contractTabs: IContractTab[] = [];

  addContractType(contractType: IContractType) {
    this._contractTypes.push(contractType);
  }

  addContractTab(contractTab: IContractTab) {
    this._contractTabs.push(contractTab);
  }

  $get = [
    "$state",
    "$stateRegistry",
    FormViewServiceName,
    serverUtilityName,
    (
      $state: StateService,
      $stateRegistry: StateRegistry,
      formViewService: FormViewService,
      serverUtility: ServerUtility
    ) => {
      $stateRegistry.register({
        name: baseState,
        url: "/:type",
        abstract: true,
        params: {
          type: undefined,
        },
        resolve: {
          contractType: [
            vsServiceName,
            "$stateParams",
            (contractService: ContractService, $stateParams: StateParams) =>
              contractService.getContractType($stateParams.type),
          ],
        },
      });

      $stateRegistry.register({
        name: createState,
        url: "/create",
        componentProvider: [
          "contractType",
          (contractType: ContractTypeService) => contractType.formComponentName,
        ],
        resolve: {
          contract: [() => { return {} }],
        },
      });

      $stateRegistry.register({
        name: editState,
        url: "/edit",
        views: {
          "": {
            componentProvider: [
              "contractType",
              (contractType: ContractTypeService) =>
                contractType.formComponentName,
            ],
          },
        },
      });

      $stateRegistry.register({
        name: gridState,
        url: "/grid",
        component: RecordGridViewComponentName,
        resolve: {
          ...getResolveObjectFromGridConfiguration([
            "contractType",
            "$injector",
            (
              contractType: ContractTypeService,
              $injector: angular.auto.IInjectorService
            ) => {
              console.log(contractType.gridConfiguration);
              const gridConfiguration = $injector.invoke(
                contractType.gridConfiguration
              );

              gridConfiguration.onAddNew = () =>
                contractType.createNavigate().then(() => {});

              return gridConfiguration;
            },
          ]),
          columns: [
            "contractType",
            "gridConfiguration",
            ColumnDefinitionServiceName,
            (
              contractType: ContractTypeService,
              gridConfiguration: GridConfiguration,
              columnDefinitionService: ColumnDefinitionService
            ) => [
              columnDefinitionService.getLinkColDef({
                colId: "info",
                headerName: "Info",
                iconClass: "fa fa-info-circle text-primary",
                stateExpression: `${contractType.infoSref()}({contractId: data.ContractInstanceId})`,
              }),
              columnDefinitionService.getLinkColDef({
                colId: "edit",
                headerName: "Edit",
                iconClass: "fa fa-pencil text-primary",
                stateExpression: `${contractType.editSref()}({contractId: data.ContractInstanceId, type: data.Type})`,
                requiresPermission: "contractEdit",
              }),
              ...(gridConfiguration.columns ?? []),
            ],
          ],
        },
      });

      $stateRegistry.register({
        name: infoState,
        url: "/{contractId:int}",
        component: machineDetailContractComponentName,
        resolve: {
          contract: [
            "$stateParams",
            "contractType",
            ($stateParams: StateParams, contractType: ContractTypeService) =>
              contractType.fetchContract($stateParams.contractId),
          ],
        },
      });

      $stateRegistry.register({
        name: infoRedirectState,
        onEnter: [
          "$state",
          "contractType",
          "$stateParams",
          (
            $state: StateService,
            contractType: ContractTypeService,
            $stateParams: StateParams
          ) =>
            $state.target(contractType.infoSref(), {
              type: $stateParams.type,
              contractId: $stateParams.contractId,
            }),
        ],
      });

      const defaultViews = {};

      this.registerContractTabs(
        $stateRegistry,
        infoState,
        this._contractTabs,
        //add default detail view
        defaultViews
      );

      const contractTypes: ContractTypeService[] = [];
      const contractService = new ContractService(
        $state,
        contractTypes,
        this._contractTabs,
        formViewService,
        serverUtility
      );
      contractTypes.push(
        ...this._contractTypes.map((ct) => {
          const typeSpecificTabs = (ct.tabs ??= []);
          this.registerContractTabs(
            $stateRegistry,
            infoState,
            typeSpecificTabs,
            defaultViews
          );
          ct.tabs = [...typeSpecificTabs, ...this._contractTabs];
          return new ContractTypeService(contractService, ct);
        })
      );

      return contractService;

      // const contractTypes = this._contractTypes.map((ct) => {
      //   const contractType: IContractType = {
      //     ...ct,
      //   };
      //   contractType.tabs = [...(ct.tabs ?? []), ...this._contractTabs];

      //   this.registerContractTabs($stateRegistry, infoState, ct.tabs ?? []);

      //   return contractType;
      // });
    },
  ];

  registerContractTabs(
    $stateRegistry: StateRegistry,
    basePath: string,
    tabs: IContractTab[],
    views?: { [key: string]: Ng1ViewDeclaration }
  ) {
    tabs.forEach((t) => {
      const stateName = (t.state.name ??= `${basePath}.${t.uiSref}`);
      t.state.views = { ...views, ...t.state.views };

      $stateRegistry.register(t.state);
      this.registerContractTabs($stateRegistry, stateName, t.children ?? []);
    });
  }
}

export class ContractService {
  constructor(
    private readonly $state: StateService,
    private readonly contractTypes: ContractTypeService[],
    private readonly contractTabs: IContractTab[],
    private readonly formViewService: FormViewService,
    private readonly serverUtility: ServerUtility
  ) {}

  getContractType(id: string): ContractTypeService | undefined {
    return this.contractTypes.find((ct) => ct.id === id);
  }

  getContractTabs(): IContractTab[] {
    return this.contractTabs;
  }

  getContractTypes(): ContractTypeService[] {
    return this.contractTypes;
  }

  gridNavigate(type: string) {
    return this.$state.go(gridState, { type });
  }

  gridSref(type: string) {
    return gridState;
  }

  infoNavigate(type: string, contractId: number) {
    return this.$state.go(this.infoSref(type), { type, contractId });
  }

  infoSref(type: string, tab?: IContractTab) {
    const contractType = this.getContractType(type);
    tab ??= contractType?.tabs?.at(0);

    if (tab) {
      return `${infoState}.${tab.uiSref}`;
    }

    return infoState;
  }

  editNavigate(type: string, contractId: number) {
    return this.$state.go(this.editSref(type), { type, contractId });
  }

  editSref(type: string) {
    return editState;
  }

  createNavigate(type: string) {
    return this.$state.go(this.createSref(type), { type });
  }

  createSref(type: string) {
    return createState;
  }

  fetchContract<TContract extends Record>(type: string, id: number) {
    const contractType = this.getContractType(type)!;

    return this.serverUtility.getById<TContract>({
      url: `/api/contract/${contractType.id}`,
      id: id,
    });
  }

  submitContract<TContract extends Record & IHasContractInstanceId>(
    type: string,
    contract: TContract,
    formViewController: FormViewController
  ) {
    
    if (!contract) {
      throw new Error("Contract object is undefined.");
    }

    const contractType = this.getContractType(type)!;
  
    const newRecord = !contract.Id;  
    const apiEndpoint = `/api/contract/${contractType.id}` + (newRecord ? "" : `/${contract.Id}`);
    
    return this.formViewService.submitForm<TContract>({
      formObject: contract,
      formViewController,
      method: newRecord ? "POST" : "PUT", 
      url: apiEndpoint,
    });
  }
  

  private getFirstInfoState(contractType: IContractType) {
    return contractType.tabs?.at(0)?.uiSref ?? infoState;
  }
}

//do this so contract service is instantiated and routes get registered as early as possible
ContractServiceRun.$inject = [vsServiceName];
export function ContractServiceRun() {}

export class ContractTypeService implements IContractType {
  constructor(
    private readonly contractService: ContractService,
    contractType: IContractType
  ) {
    Object.assign(this, contractType);
    this.tabs ??= [];
  }

  fetchContract(id: number) {
    return this.contractService.fetchContract(this.id, id);
  }

  submitContract(contract: Record & IHasContractInstanceId, formViewController: FormViewController) {
    return this.contractService.submitContract(
      this.id,
      contract,
      formViewController
    );
  }

  infoSref(tab?: IContractTab) {
    return this.contractService.infoSref(this.id, tab);
  }

  infoNavigate(contractId: number) {
    return this.contractService.infoNavigate(this.id, contractId);
  }

  gridSref() {
    this.contractService.gridSref(this.id);
  }

  gridNavigate() {
    return this.contractService.gridNavigate(this.id);
  }

  editNavigate(contractId: number) {
    return this.contractService.editNavigate(this.id, contractId);
  }

  editSref() {
    return this.contractService.editSref(this.id);
  }

  createNavigate() {
    return this.contractService.createNavigate(this.id);
  }

  createSref() {
    return this.contractService.createSref(this.id);
  }

  id!: string;
  name!: string;
  formComponentName!: string;
  detailComponentName!: string;
  gridConfiguration!: GridConfigurationFactory;
  tabs?: IContractTab[];
}

export interface IContractType {
  id: string;
  name: string;
  formComponentName: string;
  detailComponentName: string;
  gridConfiguration: GridConfigurationFactory;
  tabs?: IContractTab[];
}

export interface IHasContractInstanceId {
  ContractInstanceId: number
}

export interface IContractTab {
  uiSref: string;
  name: string;
  state: Ng1StateDeclaration;
  children?: IContractTab[];
}

// export class IContractType implements IContractType {
//   public id?: string;
//   public name?: string;
//   public formComponentName?: string;
//   public detailComponentName?: string;
//   public gridConfiguration?: GridConfiguration;
//   public tabs?: IContractTab[];
//   public contractService?: ContractService;

//   public getEditState(): string {
//     return `main.contract.${this.id}Edit`;
//   }

//   public getCreateState(): string {
//     return `main.contract.${this.id}.create`;
//   }
// }
